import { ChevronDown } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { type SubmitEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CartLink } from "@/features/cart";
import { useCategories } from "@/shared/api/generated/catalog-controller/catalog-controller";
import bookmarkIcon from "@/shared/assets/bookmark.svg";
import chevronRightIcon from "@/shared/assets/chevron-right.svg";
import globeIcon from "@/shared/assets/globe.svg";
import iconDelivery from "@/shared/assets/icon-delivery.png";
import iconInstallment from "@/shared/assets/icon-installment.png";
import iconPromo from "@/shared/assets/icon-promo.png";
import iconStores from "@/shared/assets/icon-stores.png";
import logo from "@/shared/assets/logo.svg";
import markerPin from "@/shared/assets/marker-pin.svg";
import packageIcon from "@/shared/assets/package.svg";
import phoneIcon from "@/shared/assets/phone.svg";
import rowsIcon from "@/shared/assets/rows.svg";
import searchIcon from "@/shared/assets/search.svg";
import userIcon from "@/shared/assets/user.svg";
import { LANGUAGES, setLanguage } from "@/shared/i18n";
import { cn } from "@/shared/lib/cn";
import { CatalogMenu } from "./CatalogMenu";

/** Site header from Figma "Header Actions": top bar, search row, quick links row, catalog menu. */
export function Header() {
  const { t } = useTranslation(["shop", "common"]);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const { data: categories = [] } = useCategories({ query: { staleTime: 5 * 60_000 } });
  const quickLinks = categories
    .flatMap((c) => c.pc_children)
    .filter((c) => c.pc_is_shown_in_header);

  return (
    <header className="relative z-40 bg-surface">
      {/* Stays above the catalog menu's backdrop: in Figma the header is not dimmed. */}
      <div className="relative z-50 bg-surface">
        <TopBar />
        <div className="container-page flex flex-wrap items-center gap-x-4 gap-y-3 py-3 lg:flex-nowrap lg:py-5">
          <Link to="/" className="shrink-0" aria-label={t("common:brand")}>
            <img src={logo} width={120} height={20} alt="" />
          </Link>

          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
            className="order-3 flex h-12 shrink-0 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 lg:order-none lg:px-5"
          >
            <img src={rowsIcon} width={20} height={20} alt="" />
            <span className="max-sm:sr-only">{t("catalog")}</span>
          </button>

          <SearchForm />

          <nav className="ml-auto flex items-center gap-1 text-sm text-ink lg:ml-0 lg:gap-5">
            <Link to="/cabinet" className="p-2" aria-label={t("account")}>
              <img src={userIcon} width={20} height={20} alt="" />
            </Link>
            <HeaderLink to="/cabinet/favorites" icon={bookmarkIcon} label={t("favorites")} />
            <CartLink />
            <HeaderLink to="/cabinet/orders" icon={packageIcon} label={t("orders")} />
          </nav>
        </div>
      </div>

      {/* The catalog menu opens right under the search row, over the quick-links row (Figma). */}
      <div className="relative border-b border-line">
        {menuOpen && <CatalogMenu categories={categories} onClose={closeMenu} />}
        <div className="container-page hidden items-center gap-6 pb-3 text-sm lg:flex">
          <ul className="flex shrink-0 items-center gap-5 font-medium text-ink-strong">
            <NavPromo to="/installment" icon={iconInstallment} label={t("nav.installment")} />
            <NavPromo to="/promo" icon={iconPromo} label={t("nav.promo")} />
            <NavPromo to="/delivery" icon={iconDelivery} label={t("nav.delivery")} />
            <NavPromo to="/stores" icon={iconStores} label={t("nav.stores")} />
          </ul>
          <span aria-hidden className="h-5 w-px bg-line" />
          <ul className="flex min-w-0 flex-1 items-center gap-5 overflow-hidden text-muted">
            {quickLinks.map((link) => (
              <li key={link.pc_id} className="shrink-0">
                <Link to={`/catalog/${link.pc_slug}`} className="hover:text-brand-600">
                  {link.pc_name}
                </Link>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(true);
            }}
            className="flex shrink-0 items-center gap-1 text-muted hover:text-brand-600"
          >
            {t("nav.more")}
            <img src={chevronRightIcon} width={16} height={16} alt="" />
          </button>
        </div>
      </div>
    </header>
  );
}

function TopBar() {
  const { t, i18n } = useTranslation("shop");
  const links = ["about", "career", "social", "partners"] as const;
  return (
    <div className="hidden bg-canvas text-xs text-ink lg:block">
      <div className="container-page flex h-7 items-center gap-6">
        <span className="flex items-center gap-1">
          <img src={markerPin} width={16} height={16} alt="" />
          {t("city")}
        </span>
        {links.map((key) => (
          <Link key={key} to="/legal" className="hover:text-brand-600">
            {t(`topLinks.${key}`)}
          </Link>
        ))}
        <Link to="/legal" className="ml-auto hover:text-brand-600">
          {t("topLinks.press")}
        </Link>
        <a href="tel:+998735152222" className="flex items-center gap-1 hover:text-brand-600">
          <img src={phoneIcon} width={16} height={16} alt="" />
          {t("topLinks.contacts")}
        </a>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex items-center gap-1 rounded hover:text-brand-600">
            <img src={globeIcon} width={16} height={16} alt="" />
            {t("languageName")}
            <ChevronDown className="size-4" aria-hidden />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-32 rounded-xl border border-line bg-surface p-1 text-sm shadow-lg"
            >
              {LANGUAGES.map((language) => (
                <DropdownMenu.Item
                  key={language}
                  onSelect={() => {
                    setLanguage(language);
                  }}
                  className={cn(
                    "cursor-pointer rounded-lg px-3 py-2 outline-none data-highlighted:bg-canvas",
                    i18n.language === language && "font-medium text-brand-600",
                  )}
                >
                  {t("languageName", { lng: language })}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}

/** Search goes to /search?q=… ; the field shows the current query on the results page. */
function SearchForm() {
  const { t } = useTranslation("shop");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const current = params.get("q") ?? "";

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("q");
    const q = typeof value === "string" ? value.trim() : "";
    if (q) void navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <form
      role="search"
      onSubmit={submit}
      className="order-4 flex h-12 min-w-0 flex-1 basis-full items-center rounded-xl border-2 border-brand-500 bg-surface p-1 pl-4 lg:order-none lg:basis-auto"
    >
      <input
        key={current}
        name="q"
        type="search"
        defaultValue={current}
        aria-label={t("search")}
        placeholder={t("searchPlaceholder")}
        className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-dimmed"
      />
      <button
        type="submit"
        className="flex h-full items-center gap-2 rounded-lg2 bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
      >
        <img src={searchIcon} width={18} height={18} alt="" />
        <span className="max-sm:sr-only">{t("search")}</span>
      </button>
    </form>
  );
}

function HeaderLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <Link to={to} className="flex items-center gap-2 p-2 hover:text-brand-600 lg:p-0">
      <img src={icon} width={20} height={20} alt="" />
      <span className="max-lg:sr-only">{label}</span>
    </Link>
  );
}

function NavPromo({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <li>
      <Link to={to} className="flex items-center gap-2 hover:text-brand-600">
        <img src={icon} width={20} height={20} alt="" />
        {label}
      </Link>
    </li>
  );
}
